import { sliderValueFormatContext } from './chunk-U43VNZZW.js';
import { SliderStoreFactory } from './chunk-53B74NMI.js';
import { round, clampNumber, getNumberOfDecimalPlaces } from './chunk-CVLY5S52.js';
import { IS_SAFARI } from './chunk-KKTONNDY.js';
import { setAttributeIfEmpty } from './chunk-Z6A4GYXE.js';
import { useMedia } from './chunk-ZIBTY3GT.js';
import { FocusVisibleController } from './chunk-LOFOQRT3.js';
import { $$_create_walker, $$_effect, $$_attr, $$_next_element, $$_create_template } from 'maverick.js/dom';
import { provideContext, effect, peek } from 'maverick.js';
import { defineElement, Component, ComponentController } from 'maverick.js/element';
import { ariaBool, setStyle, listenEvent, isDOMEvent, isKeyboardEvent, isUndefined } from 'maverick.js/std';

// src/player/ui/sliders/slider/api/props.ts
var sliderProps = {
  min: 0,
  max: 100,
  disabled: false,
  value: 100,
  step: 1,
  keyStep: 1,
  shiftKeyMultiplier: 5,
  trackClass: null,
  trackFillClass: null,
  trackProgressClass: null,
  thumbContainerClass: null,
  thumbClass: null
};

// src/player/ui/sliders/slider/utils.ts
function getClampedValue(min, max, value, step) {
  return clampNumber(min, round(value, getNumberOfDecimalPlaces(step)), max);
}
function getValueFromRate(min, max, rate, step) {
  const boundRate = clampNumber(0, rate, 1);
  const range = max - min;
  const fill = range * boundRate;
  const stepRatio = Math.round(fill / step);
  const steps = step * stepRatio;
  return min + steps;
}

// src/player/ui/sliders/slider/events-controller.ts
var SliderKeyDirection = {
  Left: -1,
  ArrowLeft: -1,
  Up: 1,
  ArrowUp: 1,
  Right: 1,
  ArrowRight: 1,
  Down: -1,
  ArrowDown: -1
};
var SliderEventsController = class extends ComponentController {
  constructor(instance, _delegate, _media) {
    super(instance);
    this.j = _delegate;
    this.a = _media;
  }
  onConnect() {
    effect(this.fg.bind(this));
    effect(this.gg.bind(this));
  }
  fg() {
    if (this.j.q())
      return;
    this.listen("focus", this.Na.bind(this));
    this.listen("pointerenter", this.Fb.bind(this));
    this.listen("pointermove", this.hg.bind(this));
    this.listen("pointerleave", this.Gb.bind(this));
    this.listen("pointerdown", this.ig.bind(this));
    this.listen("keydown", this.Va.bind(this));
    this.listen("keyup", this.Ua.bind(this));
  }
  gg() {
    if (this.j.q() || !this.$store.dragging())
      return;
    listenEvent(document, "pointerup", this.jg.bind(this));
    listenEvent(document, "pointermove", this.kg.bind(this));
    if (IS_SAFARI) {
      listenEvent(document, "touchmove", this.lg.bind(this), {
        passive: false
      });
    }
  }
  Na() {
    this.ca(this.$store.value());
  }
  kc(newValue, trigger) {
    const { value, min, max, dragging } = this.$store;
    const clampedValue = Math.max(min(), Math.min(newValue, max()));
    value.set(clampedValue);
    const event = this.createEvent("value-change", { detail: clampedValue, trigger });
    this.el.dispatchEvent(event);
    this.j.lc(event);
    if (dragging()) {
      const event2 = this.createEvent("drag-value-change", { detail: clampedValue, trigger });
      this.el.dispatchEvent(event2);
      this.j.mc(event2);
    }
  }
  ca(newValue, trigger) {
    const { pointerValue, min, max, dragging } = this.$store;
    const clampedValue = Math.max(min(), Math.min(newValue, max()));
    pointerValue.set(clampedValue);
    this.mg(clampedValue, trigger);
    if (dragging())
      this.kc(clampedValue, trigger);
  }
  mg(value, trigger) {
    this.dispatch("pointer-value-change", { detail: value, trigger });
  }
  sb(event) {
    let thumbPositionRate, rect = this.el.getBoundingClientRect();
    if (this.j.D === "vertical") {
      const thumbClientY = event.clientY;
      const { bottom: trackBottom, height: trackHeight } = rect;
      thumbPositionRate = (trackBottom - thumbClientY) / trackHeight;
    } else {
      const thumbClientX = event.clientX;
      const { left: trackLeft, width: trackWidth } = rect;
      thumbPositionRate = (thumbClientX - trackLeft) / trackWidth;
    }
    const { min, max } = this.$store;
    return getValueFromRate(min(), max(), thumbPositionRate, this.j.tb());
  }
  Fb() {
    this.$store.pointing.set(true);
  }
  hg(event) {
    const { dragging } = this.$store;
    if (dragging())
      return;
    this.ca(this.sb(event), event);
  }
  Gb(event) {
    this.$store.pointing.set(false);
  }
  ig(event) {
    const value = this.sb(event);
    this.Hd(value, event);
    this.ca(value, event);
  }
  Hd(value, trigger) {
    const { dragging } = this.$store;
    if (dragging())
      return;
    dragging.set(true);
    this.a.remote.pauseUserIdle(trigger);
    const event = this.createEvent("drag-start", { detail: value, trigger });
    this.el.dispatchEvent(event);
    this.j.Id(event);
  }
  Jd(value, trigger) {
    const { dragging } = this.$store;
    if (!dragging())
      return;
    dragging.set(false);
    this.a.remote.resumeUserIdle(trigger);
    const event = this.createEvent("drag-end", { detail: value, trigger });
    this.el.dispatchEvent(event);
    this.j.nc(event);
  }
  Va(event) {
    if (isDOMEvent(event)) {
      const trigger = event.trigger;
      if (isKeyboardEvent(trigger))
        event = trigger;
      else
        return;
    }
    const { key } = event;
    const { min, max } = this.$store;
    let newValue;
    if (key === "Home" || key === "PageUp") {
      newValue = min();
    } else if (key === "End" || key === "PageDown") {
      newValue = max();
    } else if (/[0-9]/.test(key)) {
      newValue = (max() - min()) / 10 * Number(key);
    }
    if (!isUndefined(newValue)) {
      this.ca(newValue, event);
      this.kc(newValue, event);
      return;
    }
    const value = this.Kd(event);
    if (!value)
      return;
    const repeat = key === this.oc;
    if (!this.$store.dragging() && repeat)
      this.Hd(value, event);
    this.ca(value, event);
    if (!repeat)
      this.kc(value, event);
    this.oc = key;
  }
  Ua(event) {
    if (isDOMEvent(event)) {
      const trigger = event.trigger;
      if (isKeyboardEvent(trigger))
        event = trigger;
      else
        return;
    }
    this.oc = "";
    const { dragging, value } = this.$store;
    if (!dragging())
      return;
    const newValue = this.Kd(event) ?? value();
    this.ca(newValue);
    this.Jd(newValue, event);
  }
  Kd(event) {
    const { key, shiftKey } = event, isValidKey = Object.keys(SliderKeyDirection).includes(key);
    if (!isValidKey)
      return;
    const { shiftKeyMultiplier } = this.$props;
    const { value } = this.$store, step = this.j.tb(), keyStep = this.j.Ld();
    const modifiedStep = !shiftKey ? keyStep : keyStep * shiftKeyMultiplier(), direction = Number(SliderKeyDirection[key]), diff = modifiedStep * direction, steps = (value() + diff) / step;
    return Number((step * steps).toFixed(3));
  }
  // -------------------------------------------------------------------------------------------
  // Document (Pointer Events)
  // -------------------------------------------------------------------------------------------
  jg(event) {
    const value = this.sb(event);
    this.ca(value, event);
    this.Jd(value, event);
  }
  lg(event) {
    event.preventDefault();
  }
  kg(event) {
    this.ca(this.sb(event), event);
  }
};

// src/player/ui/sliders/slider/slider.tsx
var $$_templ = /* @__PURE__ */ $$_create_template(`<!$><div part="track"></div>`);
var $$_templ_2 = /* @__PURE__ */ $$_create_template(`<!$><div part="track track-fill"></div>`);
var $$_templ_3 = /* @__PURE__ */ $$_create_template(`<!$><div part="track track-progress"></div>`);
var $$_templ_4 = /* @__PURE__ */ $$_create_template(`<!$><div part="thumb-container"><!$><div part="thumb"></div></div>`);
var Slider = class extends Component {
  constructor(instance) {
    super(instance);
    this.pc = false;
    this.D = "";
    // -------------------------------------------------------------------------------------------
    // Preview
    // -------------------------------------------------------------------------------------------
    this.V = null;
    provideContext(sliderValueFormatContext);
    this.a = useMedia();
    new SliderEventsController(instance, this, this.a);
    const focus = new FocusVisibleController(instance);
    this.$store.focused = focus.focused.bind(focus);
  }
  onAttach(el) {
    setAttributeIfEmpty(el, "role", "slider");
    setAttributeIfEmpty(el, "tabindex", "0");
    setAttributeIfEmpty(el, "aria-orientation", "horizontal");
    setAttributeIfEmpty(el, "autocomplete", "off");
    this.D = el.getAttribute("aria-orientation") || "";
    if (!this.pc) {
      effect(this.ng.bind(this));
      effect(this.qc.bind(this));
    }
    effect(this.rc.bind(this));
    this.og();
  }
  onConnect(el) {
    this.pg();
  }
  render() {
    const { trackClass, trackFillClass, trackProgressClass, thumbContainerClass, thumbClass } = this.$props;
    return [
      (() => {
        const [$$_root, $$_walker] = $$_create_walker($$_templ);
        $$_effect(() => $$_attr($$_root, "class", trackClass()));
        return $$_root;
      })(),
      (() => {
        const [$$_root, $$_walker] = $$_create_walker($$_templ_2);
        $$_effect(() => $$_attr($$_root, "class", trackFillClass()));
        return $$_root;
      })(),
      (() => {
        const [$$_root, $$_walker] = $$_create_walker($$_templ_3);
        $$_effect(() => $$_attr($$_root, "class", trackProgressClass()));
        return $$_root;
      })(),
      (() => {
        const [$$_root, $$_walker] = $$_create_walker($$_templ_4), $$_el = $$_next_element($$_walker);
        $$_effect(() => $$_attr($$_root, "class", thumbContainerClass()));
        $$_effect(() => $$_attr($$_el, "class", thumbClass()));
        return $$_root;
      })()
    ];
  }
  // -------------------------------------------------------------------------------------------
  // Props
  // -------------------------------------------------------------------------------------------
  tb() {
    return this.$props.step();
  }
  Ld() {
    return this.$props.keyStep();
  }
  q() {
    return this.$props.disabled();
  }
  // -------------------------------------------------------------------------------------------
  // Watch
  // -------------------------------------------------------------------------------------------
  ng() {
    const { min, max } = this.$props;
    this.$store.min.set(min());
    this.$store.max.set(max());
  }
  qc() {
    if (!this.q())
      return;
    const { dragging, pointing } = this.$store;
    dragging.set(false);
    pointing.set(false);
  }
  rc() {
    const { dragging, value, min, max } = this.$store;
    if (peek(dragging))
      return;
    value.set(getClampedValue(min(), max(), value(), this.tb()));
  }
  // -------------------------------------------------------------------------------------------
  // ARIA
  // -------------------------------------------------------------------------------------------
  qg() {
    return ariaBool(this.q());
  }
  sc() {
    return this.$props.min();
  }
  tc() {
    return this.$props.max();
  }
  rg() {
    const { value } = this.$store;
    return Math.round(value());
  }
  Md() {
    const { value, max } = this.$store;
    return round(value() / max() * 100, 2) + "%";
  }
  // -------------------------------------------------------------------------------------------
  // Attributes
  // -------------------------------------------------------------------------------------------
  og() {
    const { disabled } = this.$props;
    const {
      dragging,
      fillPercent,
      fillRate,
      interactive,
      pointerPercent,
      pointerRate,
      pointerValue,
      pointing,
      value
    } = this.$store;
    this.setAttributes({
      disabled,
      "data-dragging": dragging,
      "data-pointing": pointing,
      "data-interactive": interactive,
      "aria-disabled": this.qg.bind(this),
      "aria-valuemin": this.sc.bind(this),
      "aria-valuemax": this.tc.bind(this),
      "aria-valuenow": this.rg.bind(this),
      "aria-valuetext": this.Md.bind(this),
      "data-styled": this.sg.bind(this),
      "data-media-slider": true
    });
    this.setCSSVars({
      "--slider-fill-rate": fillRate,
      "--slider-fill-value": value,
      "--slider-fill-percent": () => fillPercent() + "%",
      "--slider-pointer-rate": pointerRate,
      "--slider-pointer-value": pointerValue,
      "--slider-pointer-percent": () => pointerPercent() + "%"
    });
  }
  sg() {
    return !!this.$props.trackClass();
  }
  pg() {
    this.V = this.el.querySelector('[slot="preview"]');
    if (!this.V)
      return;
    effect(this.tg.bind(this));
    import('./preview-JJG3ZTKT.js').then(({ setupPreviewStyles }) => {
      setupPreviewStyles(this.V, this.D);
    });
  }
  tg() {
    if (this.q() || !this.V)
      return;
    const onResize = this.ug.bind(this);
    window.requestAnimationFrame(onResize);
    const observer = new ResizeObserver(onResize);
    observer.observe(this.V);
    return () => observer.disconnect();
  }
  ug() {
    if (!this.V)
      return;
    const rect = this.V.getBoundingClientRect();
    setStyle(this.V, "--computed-width", rect.width + "px");
    setStyle(this.V, "--computed-height", rect.height + "px");
  }
  // -------------------------------------------------------------------------------------------
  // Events
  // -------------------------------------------------------------------------------------------
  lc(event) {
  }
  Id(event) {
  }
  nc(event) {
  }
  mc(event) {
  }
};
Slider.el = defineElement({
  tagName: "media-slider",
  props: sliderProps,
  store: SliderStoreFactory
});

export { Slider, sliderProps };
